// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRefreshTokenAttempt } from "./baseQueryWithRefreshTokenAttempt";
import { ENTITY, TOPICS_FILTER } from "./settings";

// code splitting: https://redux-toolkit.js.org/rtk-query/usage/code-splitting

// Define a service using a base URL and expected endpoints
export const api = createApi({
    // reducerPath: "nekashApi",
    baseQuery: baseQueryWithRefreshTokenAttempt,
    // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#manipulating-cache-behavior
    keepUnusedDataFor: 30, // this can be set for the whole api
    // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#encouraging-re-fetching-with-refetchonmountorargchange
    refetchOnMountOrArgChange: 60, // this can be set for the whole api
    // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#re-fetching-on-window-focus-with-refetchonfocus
    // refetchOnFocus: true,
    // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#re-fetching-on-network-reconnection-with-refetchonreconnect
    // refetchOnReconnect: true,
    // tagTypes: ["Users"],
    tagTypes: ["User", "Entity", "Space", "Post", "Log", "Proposal", "Profile"],
    endpoints: (builder) => ({
        // https://redux-toolkit.js.org/rtk-query/usage/mutations
        authenticate: builder.mutation({
            query: (credentials) => ({
                url: "token/get",
                method: "POST",
                // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Access-Control-Allow-Credentials
                // https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters
                credentials: "include",
                // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(credentials)`
                body: credentials,
            }),
            // transformResponse: (response) => response.token,
        }),
        refreshToken: builder.mutation({
            query: (expiredToken) => ({
                url: "token/refresh",
                method: "POST",
                credentials: "include",
                body: expiredToken,
            }),
            // transformResponse: (response) => response.token,
        }),
        vertifyToken: builder.mutation({
            query: () => ({
                url: "token/verify",
                method: "POST",
                // credentials: "include",
            }),
        }),
        verifyEmail: builder.mutation({
            query: (email) => ({
                url: "users/exist",
                method: "POST",
                body: email,
            }),
            transformResponse: (response) => response.result,
        }),
        register: builder.mutation({
            query: (registration) => ({
                url: "users/register",
                method: "POST",
                body: registration,
            }),
        }),
        requestPasswordReset: builder.mutation({
            query: (email) => ({
                url: `users/remember/${email}`,
                method: "POST",
            }),
        }),
        reset: builder.mutation({
            query: (passwordReset) => ({
                url: "users/reset",
                method: "POST",
                body: passwordReset,
            }),
        }),
        getUserProfile: builder.query({
            query: () => ({
                url: `users/profile`,
                method: "GET",
            }),
            providesTags: ["Profile"],
        }),
        editUserProfile: builder.mutation({
            query: (profile) => ({
                url: `users/profile/update`,
                method: "POST",
                body: profile,
            }),
            invalidatesTags: ["Post", "Log", "Proposal", "Profile"],
        }),
        confirmEmail: builder.mutation({
            query: (confirmation) => ({
                url: "users/confirm",
                method: "POST",
                body: confirmation,
            }),
        }),
        getUsers: builder.query({
            query: () => ({
                url: "users/list",
                method: "GET",
            }),
            // https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#invalidating-cache-data
            //providesTags: (result) =>
            //    // id is the unique id of each element as named in the response
            //    result ? [...result.map(({ id }) => ({ type: "User", id })), "User"] : ["User"],
            // https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#selectively-invalidating-lists
            providesTags: (result) =>
                result
                    ? // when the result is not empty
                      [
                          ...result.map(({ id }) => ({ type: "User", id })),
                          { type: "User", id: "LIST" },
                      ]
                    : // when the result is empty
                      [{ type: "User", id: "LIST" }],
            // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#manipulating-cache-behavior
            // keepUnusedDataFor: 5, // this can be set per endpoint
            // https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#encouraging-re-fetching-with-refetchonmountorargchange
            // refetchOnMountOrArgChange: 5 // this can be set per end point
        }),
        getUser: builder.query({
            query: (id) => ({
                url: `users/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "User", id }],
        }),
        addUser: builder.mutation({
            query: (user) => ({
                url: "users/add",
                method: "POST",
                body: user,
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        editUser: builder.mutation({
            query: ({ user, id }) => ({
                url: `users/edit/${id}`,
                method: "POST",
                body: user,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: "User", id }],
        }),
        deleteUser: builder.mutation({
            query: (id) => ({
                url: `users/delete/${id}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [{ type: "User", id }],
        }),
        toggleUserActivation: builder.mutation({
            query: (id) => ({
                url: `users/activation/${id}`,
                method: "POST",
            }),
            // https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#invalidating-cache-data
            // invalidatesTags: ["User"],
            invalidatesTags: (result, error, id) => [{ type: "User", id }],
            // invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
        }),
        removeUserPicture: builder.mutation({
            query: (id) => ({
                url: `users/removepicture/${id}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [{ type: "User", id }],
        }),
        getEntities: builder.query({
            query: () => ({
                url: "entities/list",
                method: "GET",
            }),
            providesTags: (result) =>
                // id is the unique id of each element as named in the response
                result
                    ? [
                          ...result.map(({ id }) => ({ type: "Entity", id })),
                          { type: "Entity", id: "LIST" },
                      ]
                    : [{ type: "Entity", id: "LIST" }],
        }),
        getEntity: builder.query({
            query: (id) => ({
                url: `entities/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Entity", id }],
        }),
        getEntityExperts: builder.query({
            query: (id) => ({
                url: `users/list/${id}`,
                method: "GET",
            }),
        }),
        addEntity: builder.mutation({
            query: (entity) => ({
                url: "entities/add",
                method: "POST",
                body: entity,
            }),
            invalidatesTags: [{ type: "Entity", id: "LIST" }],
        }),
        editEntity: builder.mutation({
            query: ({ entity, id }) => ({
                url: `entities/edit/${id}`,
                method: "POST",
                body: entity,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: "Entity", id }],
        }),
        verifyEntityStringID: builder.mutation({
            query: (entityID) => ({
                url: "entities/exist",
                method: "POST",
                body: entityID,
            }),
            transformResponse: (response) => response.result,
        }),
        toggleEntityActivation: builder.mutation({
            query: (id) => ({
                url: `entities/activation/${id}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [{ type: "Entity", id }],
        }),
        toggleEntityRegistration: builder.mutation({
            query: (id) => ({
                url: `entities/registration/${id}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [{ type: "Entity", id }],
        }),
        getSpaces: builder.query({
            query: () => ({
                url: "spaces/list",
                method: "GET",
            }),
            providesTags: (result) =>
                // id is the unique id of each element as named in the response
                result
                    ? [
                          ...result.map(({ id }) => ({ type: "Space", id })),
                          { type: "Space", id: "LIST" },
                      ]
                    : [{ type: "Space", id: "LIST" }],
        }),
        getSpacesByEntity: builder.query({
            query: (entity) => ({
                url: `spaces/list/${entity}`,
                method: "GET",
            }),
        }),
        getSpace: builder.query({
            query: (id) => ({
                url: `spaces/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Space", id }],
        }),
        addSpace: builder.mutation({
            query: (space) => ({
                url: "spaces/add",
                method: "POST",
                body: space,
            }),
            invalidatesTags: [{ type: "Space", id: "LIST" }],
        }),
        editSpace: builder.mutation({
            query: ({ space, id }) => ({
                url: `spaces/edit/${id}`,
                method: "POST",
                body: space,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: "Space", id }],
        }),
        deleteSpace: builder.mutation({
            query: (id) => ({
                url: `spaces/delete/${id}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, id) => [{ type: "Space", id }],
        }),
        verifySpaceStringID: builder.mutation({
            query: (spaceID) => ({
                url: "spaces/exist",
                method: "POST",
                body: spaceID,
            }),
            transformResponse: (response) => response.result,
        }),
        addTopic: builder.mutation({
            query: (topic) => ({
                url: `topics/add/${ENTITY}`,
                method: "POST",
                body: topic,
            }),
            transformResponse: (response) => response.topicId,
            invalidatesTags: ["Post", "LIST"],
        }),
        getTopic: builder.query({
            query: (id) => ({
                url: `topics/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Post", id }],
        }),
        // https://redux-toolkit.js.org/rtk-query/usage/pagination
        getTopics: builder.query({
            query: ({ filter = TOPICS_FILTER.ByLatestPost, space = null }) => ({
                url: space
                    ? `topics/list/${ENTITY}/${filter}/${space}`
                    : `topics/list/${ENTITY}/${filter}`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({ type: "Post", id })),
                          { type: "Post", id: "LIST" },
                      ]
                    : [{ type: "Post", id: "LIST" }],
        }),
        getTopicLogs: builder.query({
            query: (id) => ({
                url: `topics/log/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Log", id }],
        }),
        getTopicActiveProposal: builder.query({
            query: (id) => ({
                url: `topics/proposal/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Proposal", id }],
        }),
        upvote: builder.mutation({
            query: ({ id }) => ({
                url: `posts/upvote/${id}/${ENTITY}`,
                method: "POST",
            }),
            invalidatesTags: (result, error, { parentId }) => [
                { type: "Post", parentId },
                { type: "Log", parentId },
            ],
        }),
        //viewPost: builder.mutation({
        //    query: (id) => ({
        //        url: `posts/view/${id}`,
        //        method: "POST",
        //    }),
        //    providesTags: (result, error, id) => [{ type: "Post", id }],
        //}),
        endDate: builder.mutation({
            query: (topicDiscussionEndDate) => ({
                url: `topics/enddate/${ENTITY}`,
                method: "POST",
                body: topicDiscussionEndDate,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: "Post", id }],
        }),
        assignexpert: builder.mutation({
            query: (topicAssignedExpert) => ({
                url: `topics/assignexpert/${ENTITY}`,
                method: "POST",
                body: topicAssignedExpert,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: "Post", id }],
        }),
        //getAttachment: builder.query({
        //    query: (link) => ({
        //        url: `attachments/${link}`,
        //        method: "GET",
        //        // https://redux-toolkit.js.org/rtk-query/api/fetchBaseQuery#parsing-a-Response
        //        // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#body
        //        responseHandler: (response) => {
        //            let attachment = response.blob();
        //            // let url = URL.createObjectURL(response);
        //            return attachment;
        //        },
        //    }),
        //}),
        addOpinion: builder.mutation({
            query: (opinion) => ({
                url: `posts/addopinion/${ENTITY}`,
                method: "POST",
                body: opinion,
            }),
            transformResponse: (response) => response.logId,
            invalidatesTags: (result, error, { postID }) => [
                { type: "Post", postID },
                { type: "Log", postID },
            ],
        }),
        addProposal: builder.mutation({
            query: (proposal) => ({
                url: `posts/addproposal/${proposal.postID}/${ENTITY}`,
                method: "POST",
                body: proposal,
            }),
            transformResponse: (response) => response.logId,
            invalidatesTags: (result, error, { postID }) => [
                { type: "Post", postID },
                { type: "Log", postID },
                { type: "Proposal", postID },
            ],
        }),
        vote: builder.mutation({
            query: (vote) => ({
                url: `posts/vote/${ENTITY}`,
                method: "POST",
                body: vote,
            }),
            invalidatesTags: (result, error, arg) => {
                console.log(result);
                return [{ type: "Proposal", id: result.parentId }];
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useAuthenticateMutation,
    useRefreshTokenMutation,
    useVertifyTokenMutation,
    useVerifyEmailMutation,
    useRegisterMutation,
    useRequestPasswordResetMutation,
    useResetMutation,
    useGetUserProfileQuery,
    useEditUserProfileMutation,
    useConfirmEmailMutation,
    useGetUsersQuery,
    useGetUserQuery,
    useAddUserMutation,
    useEditUserMutation,
    useToggleUserActivationMutation,
    useRemoveUserPictureMutation,
    useDeleteUserMutation,
    useGetEntitiesQuery,
    useGetEntityQuery,
    useGetEntityExpertsQuery,
    useAddEntityMutation,
    useEditEntityMutation,
    useVerifyEntityStringIDMutation,
    useToggleEntityActivationMutation,
    useToggleEntityRegistrationMutation,
    useGetSpacesQuery,
    useGetSpacesByEntityQuery,
    useGetSpaceQuery,
    useLazyGetSpaceQuery,
    useAddSpaceMutation,
    useEditSpaceMutation,
    useDeleteSpaceMutation,
    useVerifySpaceStringIDMutation,
    useAddTopicMutation,
    useGetTopicQuery,
    useGetTopicsQuery,
    useGetTopicLogsQuery,
    useGetTopicActiveProposalQuery,
    useUpvoteMutation,
    //useViewPostMutation,
    useEndDateMutation,
    useAssignexpertMutation,
    //useGetAttachmentQuery,
    useAddOpinionMutation,
    useAddProposalMutation,
    useVoteMutation,
} = api;
